import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import AuthService from './AuthService';
import { User } from '@supabase/supabase-js';
import { supabaseClient } from '../services/supabase';
import {
    clearPasswordRecoveryPending,
    setPasswordRecoveryPending,
} from '../utils/passwordRecoveryPending';
import { hasRecoveryCallbackParams } from '../utils/recoverySession';

interface AuthContextType {
    isAuthenticated: boolean;
    authLoading: boolean;
    checkAuthStatus: () => Promise<boolean>;
    login: (email: string, password: string) => Promise<User>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);

    const syncRecoveryPending = useCallback((pending: boolean) => {
        if (pending) {
            setPasswordRecoveryPending();
        } else {
            clearPasswordRecoveryPending();
        }
    }, []);

    useEffect(() => {
        const path = window.location.pathname;
        const onResetRoute = path === '/reset-password' || path === '/password-reset';
        if (!onResetRoute && !hasRecoveryCallbackParams()) {
            clearPasswordRecoveryPending();
        }
    }, []);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const authStatus = await AuthService.checkAuthStatus();
                setIsAuthenticated(authStatus);
                if (!authStatus) {
                    syncRecoveryPending(false);
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
                setIsAuthenticated(false);
                syncRecoveryPending(false);
            } finally {
                setAuthLoading(false);
            }
        };

        initializeAuth();
    }, [syncRecoveryPending]);

    useEffect(() => {
        const { data: { subscription } } = supabaseClient.auth.onAuthStateChange((event) => {
            if (event === 'PASSWORD_RECOVERY') {
                syncRecoveryPending(true);
                setIsAuthenticated(true);
                return;
            }
            if (event === 'SIGNED_IN') {
                setIsAuthenticated(true);
            }
            if (event === 'SIGNED_OUT') {
                setIsAuthenticated(false);
                syncRecoveryPending(false);
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [syncRecoveryPending]);

    useEffect(() => {
        const sessionCheckInterval = setInterval(async () => {
            const isStillAuthenticated = await AuthService.checkAuthStatus();
            if (!isStillAuthenticated && isAuthenticated) {
                setIsAuthenticated(false);
            }
        }, 300000);

        return () => {
            clearInterval(sessionCheckInterval);
        };
    }, [isAuthenticated]);

    const login = async (email: string, password: string): Promise<User> => {
        try {
            syncRecoveryPending(false);
            const user = await AuthService.login(email, password);
            setIsAuthenticated(true);
            return user;
        } catch (error) {
            setIsAuthenticated(false);
            throw error;
        }
    };

    const logout = async (): Promise<void> => {
        try {
            await AuthService.logout();
            setIsAuthenticated(false);
            syncRecoveryPending(false);
        } catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    };

    const checkAuthStatus = async () => {
        try {
            const status = await AuthService.checkAuthStatus();
            if (!status && isAuthenticated) {
                alert('Your session has expired. Please log in again.');
            }
            if (!status) {
                syncRecoveryPending(false);
            }
            setIsAuthenticated(status);
            return status;
        } catch (error) {
            console.error('Auth check error:', error);
            setIsAuthenticated(false);
            syncRecoveryPending(false);
            return false;
        }
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            authLoading,
            checkAuthStatus,
            login,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
